import { useCallback, useEffect, useMemo, useState } from 'react'
import axios, { AxiosResponse } from 'axios'
import { PageLink, PageTitle } from '../../../_metronic/layout/core'
import DocumentTitle from 'react-document-title'
import { companyMeterList, companySitesList } from '../charts/core/_requests'
import { CompanyListItem } from '../charts/core/_models'
import Pagination from '../../utils/ResultViewer/Pagination'
import AutocompleteSelect from '../../utils/Form/Widgets/AutocompleteSelect'
import { DateRangeInput } from '../../utils/Form/Widgets/DateRangeInput'

type ReliabilityStatus = 'full' | 'partial' | 'none'

type ReliabilityResponse = {
  summary: {
    totalMeters: number
    fullCount: number
    partialCount: number
    noneCount: number
    affectedSites: number
  }
  dateColumns: { date: string; label: string }[]
  rows: {
    companyId: number
    companyName: string
    siteId: number
    siteName: string
    meterId: number
    meterName: string
    meterType: string
    activeStatus: 'active' | 'disabled'
    lastData: string | null
    daily: {
      date: string
      expectedDataCount: number
      actualDataCount: number
      missingDataCount: number
      coveragePercent: number
      status: ReliabilityStatus
    }[]
    rowStatus: ReliabilityStatus
  }[]
  pagination: {
    total: number
    page: number
    limit: number
    total_pages: number
  }
}

const breadcrumbs: Array<PageLink> = [
  {
    title: 'Data',
    path: '/data/reliability',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const formatDate = (date: Date) => date.toISOString().slice(0, 10)

const defaultDateRange = () => {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - 6)
  return { start: formatDate(start), end: formatDate(end) }
}

/** Matches daily status badges in the table when selected */
const reliabilityStatusToggleClass = (
  optionValue: '' | ReliabilityStatus,
  isSelected: boolean
): string => {
  const sizeAlign =
    'fs-8 fw-semibold px-3 py-2 lh-sm rounded-2 d-inline-flex align-items-center justify-content-center'
  if (!isSelected) {
    return `${sizeAlign} btn btn-xs btn-light text-gray-600`
  }

  switch (optionValue) {
    case '':
      return `${sizeAlign} badge badge-light-primary`
    case 'full':
      return `${sizeAlign} badge badge-light-success`
    case 'partial':
      return `${sizeAlign} badge badge-light-warning`
    case 'none':
      return `${sizeAlign} badge badge-light-danger`
    default:
      return `${sizeAlign} btn btn-xs btn-light text-gray-600`
  }
}

const DataReliabilityPage = () => {
  const initialRange = useMemo(defaultDateRange, [])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<ReliabilityResponse | null>(null)
  const [sites, setSites] = useState<CompanyListItem[]>([])
  const [meters, setMeters] = useState<CompanyListItem[]>([])
  const [page, setPage] = useState(1)

  const [companyId, setCompanyId] = useState('')
  const [siteId, setSiteId] = useState('')
  const [meterId, setMeterId] = useState('')
  const [startDate, setStartDate] = useState(initialRange.start)
  const [endDate, setEndDate] = useState(initialRange.end)
  const [statusFilter, setStatusFilter] = useState<'' | ReliabilityStatus>('')
  const [sortBy, setSortBy] = useState<'companyName' | 'siteName' | 'lastData'>('companyName')
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC')

  const validateDateRange = (start: string, end: string) => {
    if (!start || !end) {
      return 'Start date and end date are required.'
    }
    const startTs = new Date(start).getTime()
    const endTs = new Date(end).getTime()
    if (startTs > endTs) {
      return 'Start date cannot be after end date.'
    }
    const dayDiff = Math.floor((endTs - startTs) / (1000 * 60 * 60 * 24))
    if (dayDiff > 6) {
      return 'Date range cannot exceed 7 days.'
    }
    return null
  }

  const fetchReliability = useCallback(
    async (nextPage = 1) => {
      const validationError = validateDateRange(startDate, endDate)
      if (validationError) {
        setError(validationError)
        return
      }

      const params = new URLSearchParams()
      params.set('page', String(nextPage))
      params.set('start_date', startDate)
      params.set('end_date', endDate)

      if (companyId) {
        params.set('company_id', companyId)
      }
      if (siteId) {
        params.set('site_id', siteId)
      }
      if (meterId) {
        params.set('meter_id', meterId)
      }
      if (statusFilter) {
        params.set('status', statusFilter)
      }

      const sortField =
        sortBy === 'companyName'
          ? 'company_name'
          : sortBy === 'siteName'
          ? 'site_name'
          : 'last_data'
      params.set('sort_by', sortField)
      params.set('sort_dir', sortDirection.toLowerCase())

      try {
        setIsLoading(true)
        setError(null)
        const response: AxiosResponse<{ data: ReliabilityResponse }> = await axios.get(
          `/data_view/reliability/?${params.toString()}`
        )
        setData(response.data.data)
        setPage(nextPage)
      } catch (fetchError: any) {
        const fallbackMessage = 'Failed to load reliability data.'
        setError(fetchError?.response?.data?.message ?? fallbackMessage)
      } finally {
        setIsLoading(false)
      }
    },
    [companyId, siteId, meterId, startDate, endDate, statusFilter, sortBy, sortDirection]
  )

  useEffect(() => {
    if (!companyId) {
      setSites([])
      setSiteId('')
      setMeters([])
      setMeterId('')
      return
    }

    companySitesList({ companyId: Number(companyId) })
      .then((siteOptions) => {
        setSites(siteOptions)
        setSiteId('')
        setMeters([])
        setMeterId('')
      })
      .catch(() => {
        setSites([])
        setSiteId('')
        setMeters([])
        setMeterId('')
      })
  }, [companyId])

  useEffect(() => {
    if (!siteId) {
      setMeters([])
      setMeterId('')
      return
    }

    const selectedSite = [{ value: siteId, label: '' }]
    Promise.all([
      companyMeterList({ siteId: selectedSite, meterType: 'electricity' }),
      companyMeterList({ siteId: selectedSite, meterType: 'gas' }),
      companyMeterList({ siteId: selectedSite, meterType: 'water' }),
    ])
      .then(([electricityMeters, gasMeters, waterMeters]) => {
        const merged = [...electricityMeters, ...gasMeters, ...waterMeters]
        const deduped = merged.filter(
          (item, index, all) => index === all.findIndex((target) => target.value === item.value)
        )
        setMeters(deduped)
        setMeterId('')
      })
      .catch(() => {
        setMeters([])
        setMeterId('')
      })
  }, [siteId])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReliability(1)
    }, 250)
    return () => clearTimeout(timer)
  }, [fetchReliability])

  const tableRows = data?.rows ?? []

  const sortHeaderIcon = (column: typeof sortBy) => {
    if (sortBy !== column) {
      return <span className='fa fa-sort text-muted opacity-40 fs-9'></span>
    }

    return <span className={'fa ' + (sortDirection === 'ASC' ? 'fa-arrow-down' : 'fa-arrow-up')} />
  }

  const setHeaderSort = (column: 'companyName' | 'siteName' | 'lastData') => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'))
      return
    }
    setSortBy(column)
    setSortDirection('ASC')
  }

  const totalCount = data?.pagination.total ?? 0
  const limit = data?.pagination.limit ?? 20
  const viewStart = totalCount === 0 ? 0 : (page - 1) * limit + 1
  const viewEnd = Math.min(page * limit, totalCount)

  const handleDateRangeChange = (event: any) => {
    const value = event?.target?.value ?? ''
    const [start, end] = value.split(' to ')
    if (start && end) {
      setStartDate(start)
      setEndDate(end)
    }
  }

  return (
    <>
      <DocumentTitle title={process.env.REACT_APP_NAME + ' | Data Reliability'}></DocumentTitle>
      <PageTitle breadcrumbs={breadcrumbs}>Data Reliability</PageTitle>

      <div className='card mb-5 mb-xl-8'>
        <div className='card-header'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Data Reliability</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Out of {totalCount} records {viewStart} - {viewEnd} is monitoring.
            </span>
          </h3>
          <div className='align-self-end align-self-center'>
            <button
              className='btn btn-light'
              onClick={() => {
                const nextRange = defaultDateRange()
                setCompanyId('')
                setSiteId('')
                setMeterId('')
                setStatusFilter('')
                setSortBy('companyName')
                setSortDirection('ASC')
                setStartDate(nextRange.start)
                setEndDate(nextRange.end)
                setPage(1)
              }}
              disabled={isLoading}
            >
              Reset
            </button>
          </div>
        </div>
        <div className='card-body'>
          <div className='row g-4 mb-4'>
            <div className='col-md-3'>
              <AutocompleteSelect
                title='Company'
                name='company'
                onChange={(event: any) => {
                  setCompanyId(event?.target?.value ? String(event.target.value) : '')
                }}
                autoCompleteTokenUrl='/company/autocomplete'
                defaultValue={companyId}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Site</label>
              <select
                className='form-select'
                value={siteId}
                disabled={!companyId}
                onChange={(e) => setSiteId(e.target.value)}
              >
                <option value=''>All Sites</option>
                {sites.map((site) => (
                  <option key={site.value} value={site.value}>
                    {site.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Meter</label>
              <select
                className='form-select'
                value={meterId}
                disabled={!siteId}
                onChange={(e) => setMeterId(e.target.value)}
              >
                <option value=''>All Meters</option>
                {meters.map((meter) => (
                  <option key={meter.value} value={meter.value}>
                    {meter.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-3'>
              <DateRangeInput
                name='dateRange'
                title='Date Range'
                seperator='to'
                onChange={handleDateRangeChange}
                autoApply={true}
                autoRangeDays={7}
                placeholder='Select start date'
                ranges={[
                  {
                    startDate: new Date(startDate),
                    endDate: new Date(endDate),
                    key: 'selection',
                  },
                ]}
              />
            </div>
          </div>
          <div className='row g-4 mb-4'>
            <div className='col-12'>
              <label className='form-label d-block mb-1 fs-7 text-muted fw-semibold'>Status</label>
              <div
                className='d-flex flex-wrap gap-2 align-items-center'
                role='radiogroup'
                aria-label='Filter by reliability status'
              >
                {(
                  [
                    {
                      value: '' as const,
                      label: 'All',
                      id: 'reliability-status-all',
                    },
                    {
                      value: 'full' as const,
                      label: 'Full',
                      id: 'reliability-status-full',
                    },
                    {
                      value: 'partial' as const,
                      label: 'Partial',
                      id: 'reliability-status-partial',
                    },
                    {
                      value: 'none' as const,
                      label: 'None',
                      id: 'reliability-status-none',
                    },
                  ] as const
                ).map((opt) => (
                  <label key={opt.id} className='mb-0 cursor-pointer'>
                    <input
                      type='radio'
                      className='btn-check'
                      name='reliabilityStatus'
                      id={opt.id}
                      value={opt.value === '' ? '' : opt.value}
                      autoComplete='off'
                      checked={statusFilter === opt.value}
                      onChange={() => setStatusFilter(opt.value)}
                    />
                    <span
                      className={reliabilityStatusToggleClass(
                        opt.value,
                        statusFilter === opt.value
                      )}
                    >
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {error && <div className='alert alert-danger mb-4'>{error}</div>}
          <div className='card-body overflow-auto p-0'>
            {isLoading ? (
              <div className='d-flex justify-content-center p-5'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            ) : !data || tableRows.length === 0 ? (
              <div className='alert alert-info m-5'>
                No reliability data found for selected filters.
              </div>
            ) : (
              <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th role='button' className='pe-5' onClick={() => setHeaderSort('companyName')}>
                      <div className='d-flex justify-content-between align-items-center'>
                        Company
                        {sortHeaderIcon('companyName')}
                      </div>
                    </th>
                    <th role='button' className='pe-5' onClick={() => setHeaderSort('siteName')}>
                      <div className='d-flex justify-content-between align-items-center'>
                        Site
                        {sortHeaderIcon('siteName')}
                      </div>
                    </th>
                    <th>Meter</th>
                    <th>Type</th>
                    <th>Active</th>
                    <th
                      role='button'
                      className='text-nowrap'
                      onClick={() => setHeaderSort('lastData')}
                    >
                      <div className='d-flex justify-content-between align-items-center gap-2'>
                        Last Data
                        {sortHeaderIcon('lastData')}
                      </div>
                    </th>
                    {data.dateColumns.map((column) => (
                      <th key={column.date} className='text-center'>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.meterId}>
                      <td>{row.companyName}</td>
                      <td>{row.siteName}</td>
                      <td>{row.meterName}</td>
                      <td className='text-capitalize'>{row.meterType}</td>
                      <td>
                        {row.activeStatus === 'active' ? (
                          <span className='badge badge-light-success fs-8 fw-semibold'>Active</span>
                        ) : (
                          <span className='badge badge-light-danger fs-8 fw-semibold'>
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className='text-nowrap text-muted'>{row.lastData ?? '—'}</td>
                      {row.daily.map((dailyValue) => {
                        const badgeClass =
                          dailyValue.status === 'full'
                            ? 'badge-light-success'
                            : dailyValue.status === 'partial'
                            ? 'badge-light-warning'
                            : 'badge-light-danger'

                        return (
                          <td key={`${row.meterId}-${dailyValue.date}`} className='text-center'>
                            <span
                              className={`badge ${badgeClass}`}
                              title={`${dailyValue.actualDataCount}/${dailyValue.expectedDataCount}`}
                            >
                              {dailyValue.status.toUpperCase()}
                            </span>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className='card-footer'>
          {data && data.pagination.total_pages > 1 && (
            <Pagination
              page={data.pagination.page}
              limit={data.pagination.limit}
              total_count={data.pagination.total}
              setPage={(next: number) => {
                if (next < 1 || next > data.pagination.total_pages || isLoading) {
                  return
                }
                fetchReliability(next)
              }}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default DataReliabilityPage
